import { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Canonical statuses from product docs (todo.md).
 * Granted: ACTIVE, TRIALING. Blocked until billing resolved: everything else listed.
 */
const GRANTED_STATUSES = new Set(["ACTIVE", "TRIALING"]);

const BLOCKING_STATUSES = new Set([
    "TRIAL",
    "PAST_DUE",
    "CANCELED",
    "UNPAID",
    "INCOMPLETE",
    "PAUSED",
]);

function normalizeSubscriptionStatus(raw) {
    if (typeof raw !== "string") return "";
    const upper = raw.toUpperCase();
    return upper === "CANCELLED" ? "CANCELED" : upper;
}

/**
 * Returns true when a gated action should open the subscription modal instead of running.
 * Unknown/non-listed status strings defer to {@link subscriptionStatus.hasActiveSubscription}.
 */
export function subscriptionRequiresUpgrade(subscriptionStatus) {
    if (!subscriptionStatus) return true;

    const status = normalizeSubscriptionStatus(subscriptionStatus.status);

    if (GRANTED_STATUSES.has(status)) return false;
    if (BLOCKING_STATUSES.has(status)) return true;
    return subscriptionStatus.hasActiveSubscription !== true;
}

/** How to phrase the pricing modal / checkout UX when access is blocked. */
export function getSubscriptionUpgradePromptKind(subscriptionStatus) {
    if (!subscriptionRequiresUpgrade(subscriptionStatus)) return null;
    const status = normalizeSubscriptionStatus(subscriptionStatus?.status);
    return status === "TRIAL" ? "trial" : "payment";
}

export default function useSubscriptionCheck() {
    const [showModal, setShowModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const userInfo = useSelector((state) => state.userInfo.userInfo);
    const subscriptionStatus = userInfo?.subscriptionStatus;

    const needsSubscriptionUpgrade = useMemo(
        () => subscriptionRequiresUpgrade(subscriptionStatus),
        [subscriptionStatus]
    );

    const upgradePromptKind = useMemo(
        () => getSubscriptionUpgradePromptKind(subscriptionStatus),
        [subscriptionStatus]
    );

    const checkAndExecute = useCallback(
        (action) => {
            if (needsSubscriptionUpgrade) {
                setPendingAction(() => action);
                setShowModal(true);
                return false;
            }
            action();
            return true;
        },
        [needsSubscriptionUpgrade]
    );

    const handleSubscriptionSuccess = useCallback(() => {
        setShowModal(false);
        if (pendingAction) {
            pendingAction();
            setPendingAction(null);
        }
    }, [pendingAction]);

    const handleModalCancel = useCallback(() => {
        setShowModal(false);
        setPendingAction(null);
    }, []);

    return {
        showModal,
        checkAndExecute,
        handleSubscriptionSuccess,
        handleModalCancel,
        needsSubscriptionUpgrade,
        /** `trial` = encourage free trial; `payment` = payment / renew focus (null if not blocked). */
        upgradePromptKind,
        /** Alias for consumers that still expect the old prop name — not TRIAL-only. */
        isTrialUser: needsSubscriptionUpgrade,
    };
}
